<template>
  <v-row
    class="bg-blueGrey900 pa-8 ma-0 d-flex ga-4 justify-space-between flex-column flex-sm-row text-body-2"
  >
    <v-col class="flex-grow-1 order-2 order-sm-1 d-flex flex-column ga-4 justify-start">
      <h1 v-if="agentNameAndDesignation?.length" class="mb-2">
        {{ agentNameAndDesignation }}
      </h1>
      <template v-if="canadaLicenseInfo?.length">
        <div v-for="text in canadaLicenseInfo" :key="text">
          <span>{{ text }}</span>
          <br />
        </div>
      </template>
      <template v-if="usLicenseInfo.length">
        <div v-for="text in usLicenseInfo" :key="text">
          <span>{{ text }}</span>
          <br />
        </div>
      </template>
      <div
        v-if="
          agentAddress.line1 ||
          agentAddress.line2 ||
          agentAddress.line3 ||
          agentAddress.line4 ||
          agentAddress.city ||
          agentAddress.state ||
          agentAddress.zip
        "
        class="d-flex mt-5 ga-2 align-items-start justify-start"
      >
        <v-icon size="18" color="white" class="mr-2 mt-1">{{ mdiMapMarker }}</v-icon>
        <div class="d-flex flex-column">
          <p v-if="agentAddress.line1.length" class="mt-0">{{ agentAddress.line1 }}</p>
          <p v-if="agentAddress.line2.length" class="mt-0">{{ agentAddress.line2 }}</p>
          <p v-if="agentAddress.line3.length" class="mt-0">{{ agentAddress.line3 }}</p>
          <p v-if="agentAddress.line4.length" class="mt-0">{{ agentAddress.line4 }}</p>
          <p
            v-if="
              agentAddress.city.length || agentAddress.state?.length || agentAddress.zip?.length
            "
            class="mt-0"
          >
            <span v-if="agentAddress.city.length">{{ agentAddress.city }}</span>
            <span v-if="agentAddress.state">, {{ agentAddress.state }}</span>
            <span v-if="agentAddress.zip?.length"> - {{ agentAddress.zip }}</span>
          </p>
        </div>
      </div>

      <div v-if="agentDataRetreived?.preferences?.isdisplaymobilenumber" class="d-flex ga-2">
        <v-icon size="18" color="white" class="mr-2 mt-1">{{ mdiPhone }}</v-icon>
        <span>{{ agentMobile }}</span>
      </div>
      <div v-if="agentDataRetreived?.agentInfo?.email?.length">
        <a :href="composeEmailLink" class="text-decoration-none text-white d-flex ga-2 text-body-2"
          ><v-icon size="18" color="white" class="mr-2 mt-1">{{ mdiEmailBox }}</v-icon
          ><span>{{ $t(Translations.emailMe) }}</span></a
        >
      </div>
      <div v-if="agentDataRetreived?.preferences?.schedulelink">
        <a
          :href="agentDataRetreived?.preferences?.schedulelink"
          target="_blank"
          class="text-decoration-none text-white d-flex ga-2 text-body-2"
          ><v-icon size="18" color="white" class="mr-2 mt-1">{{ mdiCalendarAccount }}</v-icon
          ><span>{{ $t(Translations.scheduleAnAppointment) }}</span></a
        >
      </div>
      <a v-if="repURL" :href="repURL" target="_blank" class="text-white">{{ repURL }}</a>
    </v-col>
    <v-col
      v-if="agentDataRetreived?.preferences?.isdisplayphoto && agentDisplayPic"
      cols="auto"
      class="order-1 order-sm-2"
    >
      <img
        :src="agentDisplayPic"
        alt="agent photo"
        class="rounded-circle mx-auto"
        style="max-width: 150px !important; aspect-ratio: 1/1"
      />
    </v-col>
  </v-row>
</template>
<script lang="ts">
import type { LeadGenAgentDetails, LicenseInfo } from '@/interfaces/PegaAgent';
import { defineComponent, type PropType } from 'vue';
import { Translations } from '@/i18n/models';
import { Country } from '@/constants/Countries';
import { formatAgentTitle, formatMobileNumber } from '@/utils/common';
import { mdiCalendarAccount, mdiEmailBox, mdiMapMarker, mdiPhone } from '@mdi/js';

export default defineComponent({
  props: {
    agentDataRetreived: {
      type: Object as PropType<LeadGenAgentDetails>,
      required: true,
    },
  },
  data() {
    return {
      Translations,
      mdiMapMarker,
      mdiPhone,
      mdiEmailBox,
      mdiCalendarAccount,
    };
  },
  computed: {
    agentTitleFormat(): string {
      if (!this.agentDataRetreived.preferences.title) return '';
      return formatAgentTitle(this.agentDataRetreived.preferences.title, { normalizeCase: true });
    },
    agentDisplayPic(): string {
      return this.agentDataRetreived.preferences.photourl
        ? `${this.agentDataRetreived.preferences.photourl}?_cb=${Date.now()}`
        : '';
    },
    agentNameAndDesignation(): string {
      return this.agentDataRetreived.agentInfo.agentname || '';
    },
    agentAddress() {
      return {
        line1: this.agentDataRetreived.agentInfo.address.addressline1 ?? '',
        line2: this.agentDataRetreived.agentInfo.address.addressline2 ?? '',
        line3: this.agentDataRetreived.agentInfo.address.addressline3 ?? '',
        line4: this.agentDataRetreived.agentInfo.address.addressline4 ?? '',
        city: this.agentDataRetreived.agentInfo.address.city ?? '',
        state: this.agentDataRetreived.agentInfo.address.state ?? '',
        zip: this.agentDataRetreived.agentInfo.address.zip ?? '',
      };
    },
    agentMobile(): string {
      return formatMobileNumber(this.agentDataRetreived.preferences.mobilenumber);
    },
    services(): LicenseInfo[] {
      const licenseInfo = this.agentDataRetreived.licenseinfo;
      if (!Array.isArray(licenseInfo) || !licenseInfo.length) return [];
      return licenseInfo;
    },
    usLicenseInfo(): string[] {
      if (!this.services.length) return [];
      return this.services
        .filter((el: LicenseInfo) => el.lang.includes(Country.US))
        .map((el: LicenseInfo) => el.desc);
    },
    canadaLicenseInfo(): string[] {
      if (!this.services.length) return [];
      return this.services
        .filter((el: LicenseInfo) => el.lang.includes(Country.CA))
        .map((el: LicenseInfo) => el.desc);
    },
    repURL(): string {
      if (!this.agentDataRetreived.preferences.repwebsite) return '';
      return this.agentDataRetreived.preferences.repwebsite;
    },
    composeEmailLink(): string {
      if (!this.agentDataRetreived.agentInfo.email) return '';
      return `mailto:${this.agentDataRetreived.agentInfo.email}`;
    },
  },
});
</script>
